// FILEPATH: c:/Users/Sign Blast/Desktop/lss2-react/src/pages/Settings.js

import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Typography, Box, Grid, Paper, Checkbox, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, FormControlLabel, Switch } from '@mui/material';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Settings.css';

import { CheckBox, Visibility, VisibilityOff } from '@mui/icons-material';
import { InputAdornment, IconButton } from '@mui/material';


const Settings = () => {
  const [adminPassword, setAdminPassword] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [packageAllowance, setPackageAllowance] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#0f0f0f');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#1a1a1a');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');

  const [showControlPassword, setShowControlPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showDataUserPassword, setShowDataUserPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const checkLocalStorage = () => {
      const storedPrimary = localStorage.getItem('primaryColor');
      const storedSecondary = localStorage.getItem('secondaryColor');
      const storedDarkMode = localStorage.getItem('darkMode') === 'true';
      
      if (storedPrimary !== primaryColor) setPrimaryColor(storedPrimary);
      if (storedSecondary !== secondaryColor) setSecondaryColor(storedSecondary);
      if (storedDarkMode !== darkMode) {
        setDarkMode(storedDarkMode);
        setTextColor(storedDarkMode ? '#ffffff' : '#000000');
      }
    };
    
    const intervalId = setInterval(checkLocalStorage, 200);
    return () => clearInterval(intervalId);
  }, [primaryColor, secondaryColor, darkMode]);

  useEffect(() => {
    fetchPackageAllowance();
  }, []);
  
  const fetchPackageAllowance = async () => {
    setLoading(true); 
    try {
      const response = await fetch(`${API_BASE_URL}/users/package/data_user`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setPackageAllowance(data === 1);

    } catch (error) {
      console.error('Error fetching user package:', error);
      setError('Failed to fetch user package. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAdminPasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/users/details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ adminPassword })
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setUserDetails(data);
        setIsVerified(true);
      } else {
        setError('admin password');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred. Please try again.');
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
  
    try {
      // Validate required fields
      const requiredFields = [
        'first_name', 'last_name', 'org_name', 
        'org_email', 'phone_number', 'password',
        'control_password', 'admin_password'
      ];
      
      const missingFields = requiredFields.filter(field => 
        !userDetails[field] || userDetails[field].trim() === ''
      );
  
      if (missingFields.length > 0) {
        Swal.fire({
          title: 'Error',
          text:  `Missing required fields: ${missingFields.join(', ')}`,
          icon: 'error',
          background: primaryColor,
          color: textColor
        });
        return;
      }
  
      // Validate data user fields
      const hasDataUser = userDetails.user_data_username || userDetails.user_data_password;
      const bothDataUserFieldsPresent = 
        userDetails.user_data_username && userDetails.user_data_password

        if (hasDataUser && !bothDataUserFieldsPresent) {
          Swal.fire({
            title: 'Error',
            text: 'Data User requires both username and password',
            icon: 'error',
            background: primaryColor,
            color: textColor
          });
          return;
        }
  
        if (!hasDataUser && (userDetails.user_data_username !== null || userDetails.user_data_password !== null)) {
          Swal.fire({
            title: 'Error',
            text: 'Data User fields must both be empty or filled',
            icon: 'error', 
            background: primaryColor,
            color: textColor
          });
          return;
        }
  
      // Prepare update payload
      const updatedDetails = {
        ...userDetails,
        user_data_active: hasDataUser ? userDetails.user_data_active : null
      };

      const response = await fetch(`${API_BASE_URL}/users/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updatedDetails)
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (response.status === 409) {
        Swal.fire({
          title: 'Error',
          text: 'Data User username already exists',
          icon: 'error', 
          background: primaryColor,
          color: textColor
        })
      }

      if (response.ok) {
        Swal.fire('Success', 'User details updated successfully', 'success');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update user details');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    setUserDetails({ ...userDetails, [e.target.name]: e.target.value });
  };

  if (!isVerified) {
    return (
      <Container maxWidth="lg" className="content-page settings-container" sx={{ backgroundColor: `${primaryColor} !important`}}>
        <Typography variant="h4" component="h1" gutterBottom sx={{color: `${textColor} !important`}}>
          Verify Admin Password
        </Typography>
        <form onSubmit={handleAdminPasswordSubmit}>
          <Box mb={2}>
            <TextField
              fullWidth
              label="Admin Password"
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: `${secondaryColor} !important`,
                  '& fieldset': { borderColor: `${textColor} !important` }
                }
              }}
              InputLabelProps={{
                style: { color: `${textColor} !important` }
              }}
              InputProps={{
                style: { color: `${textColor} !important` }
              }}
              required
            />
          </Box>
          {error && (
            <Typography color="error" variant="body2" gutterBottom>
              {error}
            </Typography>
          )}
          <Button type="submit" variant="contained" className="settings-submit-btn">
            Verify
          </Button>
        </form>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="content-page settings-container" sx={{ backgroundColor: `${primaryColor} !important`}}>
      <Typography variant="h4" component="h1" gutterBottom sx={{color: `${textColor} !important`}}>
        User Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Profile and Password Section */}
        <Grid item xs={12} container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, backgroundColor: secondaryColor, color: textColor }}>
              <Typography variant="h6" gutterBottom>Profile Settings</Typography>
              <Box component="form" onSubmit={handleUpdateSubmit}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={userDetails?.first_name || ''}
                  onChange={handleInputChange}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: `${secondaryColor} !important`,
                      '& fieldset': { borderColor: `${textColor} !important` }
                    }
                  }}
                  InputLabelProps={{
                    style: { color: `${textColor} !important` }
                  }}
                  InputProps={{
                    style: { color: `${textColor} !important` }
                  }}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={userDetails?.last_name || ''}
                  onChange={handleInputChange}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: `${secondaryColor} !important`,
                      '& fieldset': { borderColor: `${textColor} !important` }
                    }
                  }}
                  InputLabelProps={{
                    style: { color: `${textColor} !important` }
                  }}
                  InputProps={{
                    style: { color: `${textColor} !important` }
                  }}
                />
                <TextField
                  fullWidth
                  label="Organization Name"
                  name="org_name"
                  value={userDetails?.org_name || ''}
                  onChange={handleInputChange}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: `${secondaryColor} !important`,
                      '& fieldset': { borderColor: `${textColor} !important` }
                    }
                  }}
                  InputLabelProps={{
                    style: { color: `${textColor} !important` }
                  }}
                  InputProps={{
                    style: { color: `${textColor} !important` }
                  }}
                />
                <TextField
                  fullWidth
                  label="Organization Email"
                  name="org_email"
                  value={userDetails?.org_email || ''}
                  onChange={handleInputChange}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: `${secondaryColor} !important`,
                      '& fieldset': { borderColor: `${textColor} !important` }
                    }
                  }}
                  InputLabelProps={{
                    style: { color: `${textColor} !important` }
                  }}
                  InputProps={{
                    style: { color: `${textColor} !important` }
                  }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  value={userDetails?.phone_number || ''}
                  onChange={handleInputChange}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: `${secondaryColor} !important`,
                      '& fieldset': { borderColor: `${textColor} !important` }
                    }
                  }}
                  InputLabelProps={{
                    style: { color: `${textColor} !important` }
                  }}
                  InputProps={{
                    style: { color: `${textColor} !important` }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, backgroundColor: secondaryColor, color: textColor }}>
              <Typography variant="h6" gutterBottom>Password Settings</Typography>
              <TextField
                fullWidth
                label="Control Password"
                name="control_password"
                type={showControlPassword ? 'text' : 'password'}
                value={userDetails?.control_password || ''}
                onChange={handleInputChange}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: `${secondaryColor} !important`,
                    '& fieldset': { borderColor: `${textColor} !important` }
                  }
                }}
                InputLabelProps={{
                  style: { color: `${textColor} !important` }
                }}
                InputProps={{
                  style: { color: `${textColor} !important` },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowControlPassword(!showControlPassword)}
                        edge="end"
                      >
                        {showControlPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                label="Admin Password"
                name="admin_password"
                type={showAdminPassword ? 'text' : 'password'}
                value={userDetails?.admin_password || ''}
                onChange={handleInputChange}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: `${secondaryColor} !important`,
                    '& fieldset': { borderColor: `${textColor} !important` }
                  }
                }}
                InputLabelProps={{
                  style: { color: `${textColor} !important` }
                }}
                InputProps={{
                  style: { color: `${textColor} !important` },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowAdminPassword(!showAdminPassword)}
                        edge="end"
                      >
                        {showAdminPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                label="Login Password"
                name="password"
                type={showLoginPassword ? 'text' : 'password'}
                value={userDetails?.password || ''}
                onChange={handleInputChange}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: `${secondaryColor} !important`,
                    '& fieldset': { borderColor: `${textColor} !important` }
                  }
                }}
                InputLabelProps={{
                  style: { color: `${textColor} !important` }
                }}
                InputProps={{
                  style: { color: `${textColor} !important` },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        edge="end"
                      >
                        {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Paper>
            <Paper sx={{ mt: 2, p: 3, backgroundColor: secondaryColor, color: textColor }}>
              <Typography variant="h6" gutterBottom>Default Theme</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={userDetails?.dark_mode === 1}
                    onChange={(e) => handleInputChange({
                      target: {
                        name: 'dark_mode',
                        value: e.target.checked ? 1 : 0
                      }
                    })}
                    color="primary"
                  />
                }
                label="Dark Mode"
                sx={{ color: textColor }}
              />
            </Paper>
          </Grid>
        </Grid>
        
        {/* User Management Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, backgroundColor: secondaryColor, color: textColor }}>
            
            <Typography variant="h6" gutterBottom>Data User Management</Typography>
            {packageAllowance ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto', mt: -10 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: textColor }}>Username</TableCell>
                      <TableCell sx={{ color: textColor }}>Password</TableCell>
                      <TableCell sx={{ color: textColor }}>Active</TableCell>
                      <TableCell sx={{ color: textColor }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(userDetails?.user_data_username || userDetails?.user_data_username === '') && (
                      <TableRow>
                        <TableCell>
                        <TextField
                          value={userDetails.user_data_username || ''}
                          onChange={(e) => handleInputChange({
                            target: { name: 'user_data_username', value: e.target.value }
                          })}
                          sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: `${secondaryColor} !important`,
                              '& fieldset': { borderColor: `${textColor} !important` }
                            },
                            '& .MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: `${textColor} !important`
                            }
                          }}
                          InputProps={{ style: { color: textColor } }}
                          fullWidth
                        />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type={showDataUserPassword ? 'text' : 'password'}
                            value={userDetails.user_data_password || ''}
                            onChange={(e) => handleInputChange({
                              target: { name: 'user_data_password', value: e.target.value }
                            })}
                            sx={{ 
                              mb: 2,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: `${secondaryColor} !important`,
                                '& fieldset': { borderColor: `${textColor} !important` }
                              }
                            }}
                            InputLabelProps={{
                              style: { color: `${textColor} !important` }
                            }}
                            InputProps={{
                              style: { color: `${textColor} !important` },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowDataUserPassword(!showDataUserPassword)}
                                    edge="end"
                                  >
                                    {showDataUserPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={userDetails.user_data_active === 1 || false}
                            onChange={(e) => handleInputChange({
                              target: { name: 'user_data_active', value: e.target.checked ? 1 : 0 }
                            })}
                            sx={{ color: textColor }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              setUserDetails(prev => ({
                                ...prev,
                                user_data_username: null,
                                user_data_password: null,
                                user_data_active: null
                              }));
                            }}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {userDetails?.user_data_username == null && (
                    <Button 
                      variant="contained" 
                      onClick={() => {
                        setUserDetails(prev => ({
                          ...prev,
                          user_data_username: userDetails?.org_email || '',
                          user_data_password: '',
                          user_data_active: 0
                        }));
                      }}
                      sx={{ mt: 2 }}
                    >
                      Add Data User
                    </Button>
                  )}
            </Box>
            ) : 
              <Typography variant="body2" sx={{ color: 'red' }} >
                Data User not available in your current package.
              </Typography>
            }
          </Paper>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button 
            type="submit" 
            variant="contained" 
            size="large"
            onClick={handleUpdateSubmit}
            sx={{ float: 'right' }}
          >
            Save All Changes
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Container>
  );
};

export default Settings;
